import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import Hls from 'hls.js';
import Go2rtcWhepPlayer from './Go2rtcWhepPlayer';
import {
  fetchStreamJpegBlob,
  isHttpUrl,
  isRtspUrl,
} from '../../utils/streamPreviewSnapshot';
import {
  aspectLockedZoneRect,
  normalizeZoneRect,
  type ZoneRect,
} from '../../utils/cctvZoneView';

export type RiskZoneRect = ZoneRect;

const SNAPSHOT_REFRESH_MS = 3000;

function normalizeRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number
): RiskZoneRect {
  return normalizeZoneRect(x0, y0, x1, y1);
}

function parseZonesJson(raw: string): RiskZoneRect[] {
  if (!raw || !String(raw).trim()) return [];
  try {
    const v = JSON.parse(String(raw).trim());
    if (!Array.isArray(v)) return [];
    const out: RiskZoneRect[] = [];
    for (const z of v) {
      if (Array.isArray(z) && z.length >= 4) {
        out.push([Number(z[0]), Number(z[1]), Number(z[2]), Number(z[3])]);
      }
    }
    return out;
  } catch {
    return [];
  }
}

export interface RiskZoneEditorProps {
  streamUrl?: string;
  /** WebRTC(WHEP) — 있으면 HLS/스냅샷보다 우선 (저장된 CCTV·게이트웨이) */
  whepUrl?: string;
  zonesJson: string;
  onZonesJsonChange: (json: string) => void;
  height?: number;
  /**
   * true: HLS 재생 대신 JPEG 스냅샷만 사용 (CCTV 등록·미저장).
   * 등록 시 id/프록시 없어 HLS가 잠깐 뜨다 끊기는 문제 방지.
   */
  preferSnapshot?: boolean;
  /** cyan=물(감시) 구역, red=특별관리구역 */
  accent?: 'cyan' | 'red';
  /** 드래그 안내 문구 앞부분 (비우면 accent 기본) */
  instructionHint?: string;
}

const ACCENT_STYLES = {
  cyan: {
    solidBorder: '2px solid rgba(0, 200, 255, 0.85)',
    dashedBorder: '2px dashed rgba(0, 200, 255, 0.9)',
    solidFill: 'rgba(0, 120, 200, 0.18)',
    dashedFill: 'rgba(0, 120, 200, 0.12)',
    defaultHint: '물 영역을 드래그해 사각형을 추가(화면 비율 유지)',
  },
  red: {
    solidBorder: '2px solid rgba(255, 23, 68, 0.95)',
    dashedBorder: '2px dashed rgba(255, 23, 68, 0.9)',
    solidFill: 'rgba(255, 23, 68, 0.2)',
    dashedFill: 'rgba(255, 23, 68, 0.12)',
    defaultHint: '특별관리구역을 드래그해 빨간 사각형을 추가(화면 비율 유지)',
  },
} as const;

/**
 * CCTV 영상(또는 RTSP/등록용 스냅샷) 위에 구역 사각형을 드래그로 지정.
 * 좌표는 영상 픽셀 기준.
 */
const RiskZoneEditor: React.FC<RiskZoneEditorProps> = ({
  streamUrl,
  whepUrl = '',
  zonesJson,
  onZonesJsonChange,
  height = 320,
  preferSnapshot = false,
  accent = 'cyan',
  instructionHint,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const snapshotUrlRef = useRef<string | null>(null);
  const loadGenRef = useRef(0);

  const [zones, setZones] = useState<RiskZoneRect[]>(() => parseZonesJson(zonesJson));
  const [drag, setDrag] = useState<{
    startX: number;
    startY: number;
    curX: number;
    curY: number;
  } | null>(null);
  const [mediaSize, setMediaSize] = useState({ w: 0, h: 0 });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [snapshotSrc, setSnapshotSrc] = useState<string | null>(null);
  const [useSnapshot, setUseSnapshot] = useState(false);
  const [useWebrtc, setUseWebrtc] = useState(false);
  const webrtcFallbackRef = useRef(false);
  const accentStyle = ACCENT_STYLES[accent] || ACCENT_STYLES.cyan;

  const whep = (whepUrl || '').trim();

  useEffect(() => {
    webrtcFallbackRef.current = false;
    setUseWebrtc(Boolean(whep));
  }, [whep]);

  const syncWebrtcVideoSize = useCallback(() => {
    const v = wrapRef.current?.querySelector('video');
    if (v && v.videoWidth > 0) {
      setMediaSize({ w: v.videoWidth, h: v.videoHeight });
      setLoading(false);
      setLoadError(null);
    }
  }, []);

  useEffect(() => {
    setZones(parseZonesJson(zonesJson));
  }, [zonesJson]);

  const emitZones = useCallback(
    (next: RiskZoneRect[]) => {
      setZones(next);
      onZonesJsonChange(JSON.stringify(next));
    },
    [onZonesJsonChange]
  );

  const clearSnapshot = useCallback(() => {
    if (snapshotUrlRef.current) {
      URL.revokeObjectURL(snapshotUrlRef.current);
      snapshotUrlRef.current = null;
    }
    setSnapshotSrc(null);
  }, []);

  const loadSnapshotFrame = useCallback(async (url: string, gen: number, opts?: { quiet?: boolean }) => {
    const quiet = Boolean(opts?.quiet);
    if (!quiet) {
      setLoading(true);
      setLoadError(null);
    }
    try {
      const blob = await fetchStreamJpegBlob(url);
      if (gen !== loadGenRef.current) return;
      const obj = URL.createObjectURL(blob);
      if (snapshotUrlRef.current) URL.revokeObjectURL(snapshotUrlRef.current);
      snapshotUrlRef.current = obj;
      setSnapshotSrc(obj);
      setUseSnapshot(true);
      setLoadError(null);
      setLoading(false);
    } catch (err) {
      if (gen !== loadGenRef.current) return;
      setLoading(false);
      setUseSnapshot(true);
      // 이미 프레임이 있으면 갱신 실패는 조용히 (스피너만 도는 상태 방지)
      if (!quiet || !snapshotUrlRef.current) {
        setLoadError(
          err instanceof Error ? err.message : '스냅샷을 불러오기 실패'
        );
      }
    }
  }, []);

  useEffect(() => {
    if (whep && useWebrtc && !webrtcFallbackRef.current) {
      setLoading(true);
      setLoadError(null);
      setUseSnapshot(false);
      clearSnapshot();
      return;
    }

    const video = videoRef.current;
    const gen = ++loadGenRef.current;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (video) {
      video.removeAttribute('src');
      video.load();
    }

    if (!streamUrl || !streamUrl.trim()) {
      clearSnapshot();
      setMediaSize({ w: 0, h: 0 });
      setUseSnapshot(false);
      setLoadError('스트림 URL을 입력하면 영상 위에 구역을 그리기 가능.');
      setLoading(false);
      return;
    }

    const url = streamUrl.trim();
    setLoadError(null);
    setMediaSize({ w: 0, h: 0 });

    const useSnap =
      preferSnapshot || isRtspUrl(url) || !isHttpUrl(url);

    // 등록·RTSP: 안정적인 JPEG 스냅샷 (주기 갱신 — 갱신 중 스피너 재점등 안 함)
    if (useSnap) {
      setUseSnapshot(true);
      void loadSnapshotFrame(url, gen, { quiet: false });
      const timer = window.setInterval(() => {
        void loadSnapshotFrame(url, loadGenRef.current, { quiet: true });
      }, SNAPSHOT_REFRESH_MS);
      return () => {
        window.clearInterval(timer);
        //  StrictMode 재실행 시 이전 gen만 무효화 (최신 스냅샷은 유지)
        if (loadGenRef.current === gen) {
          loadGenRef.current += 1;
        }
      };
    }

    // 수정 모드 + HTTP(S)/HLS: 브라우저 재생
    setUseSnapshot(false);
    clearSnapshot();
    setLoading(true);

    const fallbackSnapshot = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setUseSnapshot(true);
      void loadSnapshotFrame(url, loadGenRef.current);
    };

    const applyVideoSize = (el: HTMLVideoElement) => {
      if (el.videoWidth > 0) {
        setMediaSize({ w: el.videoWidth, h: el.videoHeight });
        setLoading(false);
      }
    };

    if (url.includes('.m3u8') && Hls.isSupported()) {
      let cancelled = false;
      const start = () => {
        const v = videoRef.current;
        if (!v) {
          window.requestAnimationFrame(start);
          return;
        }
        if (cancelled) return;
        const hls = new Hls({
          lowLatencyMode: false,
          liveSyncDurationCount: 3,
          enableWorker: true,
          backBufferLength: 30,
          maxBufferLength: 15,
          fragLoadingMaxRetry: 4,
          manifestLoadingMaxRetry: 4,
        });
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(v);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          applyVideoSize(v);
          v.play().catch(() => {
            v.muted = true;
            v.play().catch(() => undefined);
          });
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) fallbackSnapshot();
        });
      };
      start();
      const failSafe = window.setTimeout(fallbackSnapshot, 12000);
      return () => {
        cancelled = true;
        window.clearTimeout(failSafe);
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    }

    if (video) {
      video.src = url;
      video.onloadedmetadata = () => applyVideoSize(video);
      video.onerror = () => fallbackSnapshot();
      video.play().catch(() => undefined);
    } else {
      fallbackSnapshot();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl, preferSnapshot, clearSnapshot, loadSnapshotFrame, whep, useWebrtc]);

  // 언마운트 시에만 스냅샷 URL 해제
  useEffect(() => () => clearSnapshot(), [clearSnapshot]);

  const onVideoMeta = () => {
    const v = videoRef.current;
    if (v && v.videoWidth > 0) {
      setMediaSize({ w: v.videoWidth, h: v.videoHeight });
      setLoading(false);
    }
  };

  const onImgLoad = () => {
    const img = imgRef.current;
    if (img && img.naturalWidth > 0) {
      setMediaSize({ w: img.naturalWidth, h: img.naturalHeight });
      setLoading(false);
    }
  };

  const clientToMedia = (clientX: number, clientY: number) => {
    const wrap = wrapRef.current;
    if (!wrap || mediaSize.w <= 0) return { x: 0, y: 0 };
    const rect = wrap.getBoundingClientRect();
    const scale = Math.min(rect.width / mediaSize.w, rect.height / mediaSize.h);
    const dispW = mediaSize.w * scale;
    const dispH = mediaSize.h * scale;
    const ox = (rect.width - dispW) / 2;
    const oy = (rect.height - dispH) / 2;
    const x = Math.max(0, Math.min(mediaSize.w, (clientX - rect.left - ox) / scale));
    const y = Math.max(0, Math.min(mediaSize.h, (clientY - rect.top - oy) / scale));
    return { x, y };
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (mediaSize.w <= 0) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const { x, y } = clientToMedia(e.clientX, e.clientY);
    setDrag({ startX: x, startY: y, curX: x, curY: y });
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const { x, y } = clientToMedia(e.clientX, e.clientY);
    const locked = aspectLockedZoneRect(
      drag.startX,
      drag.startY,
      x,
      y,
      mediaSize.w,
      mediaSize.h
    );
    const endX = x >= drag.startX ? locked[2] : locked[0];
    const endY = y >= drag.startY ? locked[3] : locked[1];
    setDrag({ ...drag, curX: endX, curY: endY });
  };

  const handlePointerUp = () => {
    if (!drag) return;
    const rect = aspectLockedZoneRect(
      drag.startX,
      drag.startY,
      drag.curX,
      drag.curY,
      mediaSize.w,
      mediaSize.h
    );
    setDrag(null);
    const w = rect[2] - rect[0];
    const h = rect[3] - rect[1];
    if (w < 8 || h < 8) return;
    emitZones([...zones, rect]);
  };

  const removeZone = (index: number) => {
    emitZones(zones.filter((_, i) => i !== index));
  };

  const clearZones = () => emitZones([]);

  const wrapEl = wrapRef.current;
  const displayScale =
    mediaSize.w > 0 && wrapEl
      ? Math.min(wrapEl.clientWidth / mediaSize.w, height / mediaSize.h)
      : mediaSize.w > 0
        ? 1
        : 1;

  const renderZone = (z: RiskZoneRect, key: string, dashed?: boolean) => {
    const [x1, y1, x2, y2] = z;
    return (
      <Box
        key={key}
        sx={{
          position: 'absolute',
          left: x1 * displayScale,
          top: y1 * displayScale,
          width: (x2 - x1) * displayScale,
          height: (y2 - y1) * displayScale,
          border: dashed ? accentStyle.dashedBorder : accentStyle.solidBorder,
          backgroundColor: dashed ? accentStyle.dashedFill : accentStyle.solidFill,
          pointerEvents: 'none',
          boxSizing: 'border-box',
        }}
      />
    );
  };

  const dispW = mediaSize.w * displayScale;
  const dispH = mediaSize.h * displayScale;
  const showVideo = (!useSnapshot && mediaSize.w > 0) || (useWebrtc && Boolean(whep));
  const showImg = useSnapshot && Boolean(snapshotSrc);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {preferSnapshot || (isRtspUrl(streamUrl || '') && !whep)
          ? `스냅샷 위에 ${instructionHint || accentStyle.defaultHint}`
          : whep && useWebrtc
            ? `실시간(WebRTC) 영상 위에 ${instructionHint || accentStyle.defaultHint}`
            : instructionHint || accentStyle.defaultHint}
      </Typography>
      {loadError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {loadError}
        </Alert>
      )}
      <Box
        ref={wrapRef}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          height,
          bgcolor: '#111',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        {loading && !snapshotSrc && (
          <CircularProgress size={36} sx={{ position: 'absolute', zIndex: 2 }} />
        )}
        {loading && snapshotSrc && (
          <CircularProgress
            size={20}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, opacity: 0.7 }}
          />
        )}
        {whep && useWebrtc ? (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
            <Go2rtcWhepPlayer
              whepUrl={whep}
              muted
              style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#111' }}
              onFallback={() => {
                webrtcFallbackRef.current = true;
                setUseWebrtc(false);
              }}
              onPlaying={syncWebrtcVideoSize}
            />
          </Box>
        ) : null}
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          onLoadedMetadata={onVideoMeta}
          style={{
            width: dispW || '100%',
            height: dispH || '100%',
            objectFit: 'contain',
            // display:none 이면 metadata/재생이 깨질 수 있어 visibility로 숨김
            visibility: showVideo ? 'visible' : 'hidden',
            position: showVideo ? 'relative' : 'absolute',
            pointerEvents: 'none',
          }}
        />
        {snapshotSrc && (
          <img
            ref={imgRef}
            src={snapshotSrc}
            alt="구역 편집용 스냅샷"
            onLoad={onImgLoad}
            style={{
              width: dispW || '100%',
              height: dispH || '100%',
              objectFit: 'contain',
              display: showImg ? 'block' : 'none',
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            width: dispW || '100%',
            height: dispH || '100%',
            cursor: mediaSize.w > 0 ? 'crosshair' : 'default',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {zones.map((z, i) => renderZone(z, `z-${i}`))}
          {drag &&
            renderZone(
              aspectLockedZoneRect(
                drag.startX,
                drag.startY,
                drag.curX,
                drag.curY,
                mediaSize.w,
                mediaSize.h
              ),
              'draft',
              true
            )}
        </Box>
      </Box>
      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button size="small" variant="outlined" onClick={clearZones} disabled={zones.length === 0}>
          구역 모두 지우기
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!streamUrl?.trim()}
          onClick={() => {
            if (!streamUrl?.trim()) return;
            void loadSnapshotFrame(streamUrl.trim(), loadGenRef.current);
          }}
        >
          스냅샷 새로고침
        </Button>
        <Typography variant="caption" color="text.secondary">
          {zones.length}개 구역
          {mediaSize.w > 0 ? ` · ${mediaSize.w}×${mediaSize.h}px` : ''}
        </Typography>
      </Box>
      {zones.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {zones.map((z, i) => (
            <Typography key={`lbl-${i}`} variant="caption" display="block" color="text.secondary">
              #{i + 1} [{z.map((n) => Math.round(n)).join(', ')}]{' '}
              <Button size="small" onClick={() => removeZone(i)} sx={{ minWidth: 0, p: 0.25 }}>
                삭제
              </Button>
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RiskZoneEditor;
